"use client"
import throttle from "lodash/throttle";
import cn from "classnames";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import useMusicStore, { CmsSiteSettingsMusic, parseAndBuildArtist, Track } from '@/state/music';
import { VideoPlayerHandle, VideoPlayerProps } from "@/lib/yt";
import { isIos } from "@/lib/utils";
import { fetchCMS } from "@/lib/cms";
import Crawler from "@/components/Crawler";
import VideoPlayer from "@/components/VideoPlayer";
import { Welcome } from "@/components/Welcome";
import { SvgIcon } from "./ui/SvgIcon";
import Styles from "./musicPlayer.module.scss";

export type MusicPlayerProps = {
  className?: string;
};

export const MusicPlayer = ({ className }: MusicPlayerProps) => {
  const hiddenRef = useRef<VideoPlayerHandle | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expandTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const volumeSliderTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  let volumeIconClass: "" | "muted" | "low" | "medium" | "high" = "";;

  const { setPlayIntent, setCurrentTrackId, setPlayerState, setPlaylist, setPlayerRef } = useMusicStore.getState();
  const currentTrackId = useMusicStore((s) => s.currentTrackId);
  const playlist = useMusicStore((s) => s.playlist);
  const currentTrack = useMusicStore(s => s.getCurrentTrack());
  const playerState = useMusicStore(s => s.playerState);
  const playerRef = useMusicStore(s => s.playerRef);

  const [volumeIconVisible, setVolumeIconVisible] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(50);
  const [muted, setMuted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [meta, setMeta] = useState<{ author: string; title: string } | null>(null);
  const [mounted, setMounted] = useState(false);

  const videoIdToUse = currentTrackId;
  const videoOptions: VideoPlayerProps['options'] = { playerVars: { start: currentTrack?.youtube.start ?? 0, end: currentTrack?.youtube.end } };
  const showVolumeSlider = mounted && !isIos();

  const outboundLink = useMemo(() => {
    if (!meta || !videoIdToUse) return '#';
    return currentTrack?.promoLink ?? `https://youtube.com/watch?v=${currentTrackId}`;
  }, [meta, videoIdToUse, currentTrack?.promoLink, currentTrackId]);

  const play = () => {
    setPlayIntent(true);
    playerRef?.play();
  };

  const togglePlay = () => {
    setPlayIntent(playerState !== 1);
    playerRef?.togglePlay();
  };

  const onHover = () => {
    if (expandTimer.current) {
      clearTimeout(expandTimer.current);
      expandTimer.current = null;
    }
    setIsExpanded(true);
  };

  const onMouseOut = () => {
    scheduleCollapse();
  };

  const onThumbTouchEnd = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      scheduleCollapse();
    } else {
      if (expandTimer.current) {
        clearTimeout(expandTimer.current);
        expandTimer.current = null;
      }
      setIsExpanded(false);
    }
  };

  const scheduleCollapse = () => {
    expandTimer.current = setTimeout(setIsExpanded, 3000, false);
  };

  const pollMeta = useCallback(() => {
    const data = playerRef?.getVideoData?.();
    if (data && (meta?.author !== data.author || meta?.title !== data.title)) {
      setMeta({ author: data.author, title: data.title });
    }
  }, [meta?.author, meta?.title, playerRef]);

  const fetchPlaylist = useCallback(async () => {
    const { data: items } = (await fetchCMS('playlists', { populate: '[youtube][populate]=*', normalize: true })) as unknown as { data: Track[] };
    if (!items || !items.length) return;
    const playlist: Track[] = items.map(parseAndBuildArtist);
    setPlaylist(playlist);
  }, [setPlaylist]);

  const fetchCurrentTrack = useCallback(async () => {
    const { data } = (await fetchCMS('site-setting', { populate: '[music][populate][youtube]=*', normalize: true })) as { data: { music: CmsSiteSettingsMusic }; };
    const vid = data?.music?.youtube?.vid;
    if (!vid) return;
    setCurrentTrackId(vid);
  }, [setCurrentTrackId]);

  const onVolumeHover = () => {
    resetVolumeSliderHiderTimer();
    if (isExpanded) {
      setVolumeIconVisible(true);
    }
  };
  
  const onVolumeIconTouch = () => {
    setVolumeIconVisible(!volumeIconVisible);
  };

  const resetVolumeSliderHiderTimer = () => {
    clearTimeout(volumeSliderTimer.current!);
  };

  const hideVolumeSlider = (delay: number = 2000) => {
    volumeSliderTimer.current = setTimeout(setVolumeIconVisible, delay, false);
  };

  const onVolumeSliderHide: React.MouseEventHandler = () => {
    hideVolumeSlider();
  };

  const onVolumeSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const volume = Number(raw);
    const safe = Number.isFinite(volume) ? Math.max(0, Math.min(100, Math.round(volume))) : 0;

    setVolumeLevel(safe);
    if (safe > 0) {
      setMuted(false);
    }
  };

  const setPlayerVolume = useMemo(
      () => throttle((v: number) => {
        playerRef?.setVolume(v)
      }, 100),
    [playerRef]
  );

  const onVolumeIconClick = () => {
    if (!playerRef) return;
    if (playerRef.isMuted()) {
      setMuted(false);
    } else {
      setMuted(true);
    }
  };

  if (muted) volumeIconClass = "muted";
  else if (volumeLevel > 66) volumeIconClass = "high";
  else if (volumeLevel > 33) volumeIconClass = "medium";
  else if (volumeLevel > 0)  volumeIconClass = "low";
  else volumeIconClass = "muted";

  useEffect(() => {
    if (typeof currentTrack?.volume === "number") {
      setVolumeLevel(currentTrack.volume);
    }
  }, [currentTrack?.volume]);

  useEffect(() => {
    if (!playerRef) return;
    if (muted) {
      playerRef.mute();
    } else {
      playerRef.unmute();
    }
  }, [muted, playerRef]);

  useEffect(() => {
    if (playerRef) {
      setPlayerVolume(volumeLevel);
    }
  }, [volumeLevel, playerRef, setPlayerVolume]);

  useEffect(() => {
    if (!playlist.length) fetchPlaylist();
  }, [fetchPlaylist, playlist.length]);

  useEffect(() => {
    if (!currentTrackId) {
      console.log("No currentTrackId");
      fetchCurrentTrack();
    }
  }, [currentTrackId, fetchCurrentTrack]);

  useEffect(() => {
    pollIntervalRef.current = setInterval(pollMeta, 1000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [pollMeta]);

  // Use a callback ref so we reliably set the global playerRef when VideoPlayer attaches
  const handleHiddenRef = useCallback((r: VideoPlayerHandle | null) => {
    hiddenRef.current = r;
    setPlayerRef(r);
  }, [setPlayerRef]);

  useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (playerState === 1) {
      if (expandTimer.current) {
        clearTimeout(expandTimer.current);
      }
      setIsExpanded(true);
    }
    scheduleCollapse();
  }, [playerState]);

  return (
    <div className={cn(Styles.musicPlayerWrapper, className, {  isPlaying: playerState === 1, isExpanded, volumeIconVisible })}>
      <div 
        onMouseEnter={onHover}
        onMouseLeave={onMouseOut}
        className={Styles.musicPlayer}
      >
        <VideoPlayer
          ref={handleHiddenRef}
          videoId={videoIdToUse}
          volume={currentTrack?.volume}
          options={videoOptions}
          className={Styles.hidden}
          onStateChange={setPlayerState}
          onFirstFrame={setIsExpanded.bind(null, true)}
          ariaHidden={true}
        />
        <div
          onTouchEnd={onThumbTouchEnd}
          className={Styles.note}
          onMouseEnter={onVolumeHover}
          onMouseLeave={onVolumeSliderHide}
        >
          {!!videoIdToUse.length &&
            /* eslint-disable-next-line @next/next/no-img-element */
            <img
              className={Styles.thumb}
              src={`https://img.youtube.com/vi/${videoIdToUse}/mqdefault.jpg`}
              width={320}
              height={180}
              alt={`${meta?.author} - ${meta?.title}`}
            />
          }
          {showVolumeSlider && (
            <SvgIcon
              onClick={onVolumeIconClick}
              onTouchEnd={onVolumeIconTouch}
              className={cn(Styles.volumeIcon, Styles[volumeIconClass])}
              iconName="volume"
              size={32}
            />
          )}
        </div>
        <a href={outboundLink} target="_blank" title={`Go to ${meta?.author ?? 'artist'}'s page`} rel="noopener noreferrer" className={Styles.meta}>
          <Crawler duration={10} className={Styles.title}>{meta?.title}</Crawler>
          <Crawler duration={5} className={Styles.author}>{meta?.author}</Crawler>
        </a>
        <button onClick={togglePlay} className={Styles.playToggle} aria-pressed={playerState === 1} />
      </div>
      <Welcome play={play} />
      {showVolumeSlider && (
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={Number.isFinite(volumeLevel) ? volumeLevel : 0}
          aria-label="volume bar"
          onChange={onVolumeSliderChange}
          onMouseEnter={onVolumeHover}
          onMouseLeave={onVolumeSliderHide}
          className={cn(Styles.volumeSlider, { [Styles.muted]: muted })}
        />
      )}
    </div>
  );
};

MusicPlayer.displayName = "MusicPlayer";

export default MusicPlayer;
